import * as React from 'react';
import * as cx from 'classnames';
import { withStyles, WithStyles, createStyles, Theme } from '@material-ui/core';
import { capitalize } from '@material-ui/core/utils/helpers';
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints';

import units from 'ha/theme/units';

export const styles = (theme: Theme) =>
  createStyles({
    /* Styles applied to the root element. */
    root: {
      width: '100%',
      marginLeft: 'auto',
      boxSizing: 'border-box',
      marginRight: 'auto',
      paddingLeft: units.rem(0.5),
      paddingRight: units.rem(0.5),
      [theme.breakpoints.up('sm')]: {
        paddingLeft: units.rem(1),
        paddingRight: units.rem(1),
      },
      [theme.breakpoints.up('md')]: {
        paddingLeft: units.rem(2),
        paddingRight: units.rem(2),
      },
    },
    /* Styles applied to the root element if `fixed={true}`. */
    fixed: Object.keys(theme.breakpoints.values).reduce((acc, breakpoint) => {
      const value = theme.breakpoints.values[breakpoint];

      if (value !== 0) {
        acc[theme.breakpoints.up(breakpoint as Breakpoint | number)] = {
          maxWidth: value,
        };
      }
      return acc;
    }, {}),
    /* Styles applied to the root element if `maxWidth="xs"`. */
    maxWidthXs: {
      [theme.breakpoints.up('xs')]: {
        maxWidth: Math.max(theme.breakpoints.values.xs, 444),
      },
    },
    /* Styles applied to the root element if `maxWidth="sm"`. */
    maxWidthSm: {
      [theme.breakpoints.up('sm')]: {
        maxWidth: theme.breakpoints.values.sm,
      },
    },
    /* Styles applied to the root element if `maxWidth="md"`. */
    maxWidthMd: {
      [theme.breakpoints.up('md')]: {
        maxWidth: theme.breakpoints.values.md,
      },
    },
    /* Styles applied to the root element if `maxWidth="lg"`. */
    maxWidthLg: {
      [theme.breakpoints.up('lg')]: {
        maxWidth: theme.breakpoints.values.lg,
      },
    },
    /* Styles applied to the root element if `maxWidth="xl"`. */
    maxWidthXl: {
      [theme.breakpoints.up('xl')]: {
        maxWidth: theme.breakpoints.values.xl,
      },
    },
  });

interface Container {
  fixed?: boolean;
  className?: string;
  children: React.ReactNode;
  component?: React.ComponentType<{ className: string }>;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
}

const Container: React.SFC<Container & WithStyles<typeof styles>> = ({
  classes,
  className,
  fixed = false,
  maxWidth = 'lg',
  component: Component = 'div',
  ...props
}) => (
  <Component
    className={cx(
      classes.root,
      {
        [classes.fixed]: fixed,
        [classes[`maxWidth${capitalize(String(maxWidth))}`]]:
          maxWidth !== false,
      },
      className,
    )}
    {...props}
  />
);

export default withStyles(styles, { name: 'MuiContainer' })(Container);
